import { useCallback, useState } from "react";
import config from "@/config";
import type {
  StructuredOutputRerunApplyRequestBody,
  StructuredOutputRerunApplyResponse,
  StructuredOutputRerunPreviewRequestBody,
  StructuredOutputRerunPreviewResponse,
} from "@/types/structured-output-rerun";

async function postJson<T>(path: string, body: unknown): Promise<T> {
  const res = await fetch(`${config.API_BASE_URL}${path}`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    let detail = "";
    try {
      const data = (await res.json()) as { detail?: unknown };
      if (typeof data.detail === "string") detail = data.detail;
    } catch {
      // body wasn't JSON; ignore
    }
    throw new Error(detail || `${path} failed: ${res.status}`);
  }
  return (await res.json()) as T;
}

export function useStructuredOutputRerunPreview() {
  const [result, setResult] =
    useState<StructuredOutputRerunPreviewResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const run = useCallback(async (body: StructuredOutputRerunPreviewRequestBody) => {
    setLoading(true);
    setError(null);
    try {
      const data = await postJson<StructuredOutputRerunPreviewResponse>(
        "/api/dashboard/structured-output-rerun/preview",
        body,
      );
      setResult(data);
      return data;
    } catch (err) {
      const msg = err instanceof Error ? err.message : "preview failed";
      setError(msg);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const reset = useCallback(() => {
    setResult(null);
    setError(null);
  }, []);

  return { result, loading, error, run, reset };
}

export function useStructuredOutputRerunApply() {
  const [result, setResult] =
    useState<StructuredOutputRerunApplyResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const run = useCallback(async (body: StructuredOutputRerunApplyRequestBody) => {
    setLoading(true);
    setError(null);
    try {
      const data = await postJson<StructuredOutputRerunApplyResponse>(
        "/api/dashboard/structured-output-rerun/apply",
        body,
      );
      setResult(data);
      return data;
    } catch (err) {
      const msg = err instanceof Error ? err.message : "apply failed";
      setError(msg);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const reset = useCallback(() => {
    setResult(null);
    setError(null);
  }, []);

  return { result, loading, error, run, reset };
}
